import { STORAGE_KEYS } from './constants';
import { AnalyticsEvent, InstrumentType } from './types';

/**
 * Generate a unique session ID
 */
export function generateSessionId(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
}

/**
 * Get or create session ID from localStorage
 */
export function getSessionId(): string {
  try {
    let sessionId = localStorage.getItem(STORAGE_KEYS.SESSION_ID);
    
    if (!sessionId) {
      sessionId = generateSessionId();
      localStorage.setItem(STORAGE_KEYS.SESSION_ID, sessionId);
      localStorage.setItem(STORAGE_KEYS.SESSION_START, Date.now().toString());
    }
    
    return sessionId;
  } catch (error) {
    console.error('[Analytics Utils] Error getting session ID:', error);
    return generateSessionId();
  }
}

/**
 * Clear session data
 */
export function clearSessionData(): void {
  try {
    localStorage.removeItem(STORAGE_KEYS.SESSION_ID);
    localStorage.removeItem(STORAGE_KEYS.SESSION_START);
  } catch (error) {
    console.error('[Analytics Utils] Error clearing session data:', error);
  }
}

/**
 * Get session start time
 */
export function getSessionStartTime(): number {
  try {
    const startTime = localStorage.getItem(STORAGE_KEYS.SESSION_START);
    return startTime ? parseInt(startTime, 10) : Date.now();
  } catch (error) {
    console.error('[Analytics Utils] Error getting session start time:', error);
    return Date.now();
  }
}

/**
 * Detect device type based on screen width and user agent
 */
export function getDeviceType(): 'desktop' | 'mobile' | 'tablet' {
  const width = window.innerWidth;
  const userAgent = navigator.userAgent.toLowerCase();

  if (width < 768) {
    return 'mobile';
  } else if (width < 1024 || /tablet|ipad/i.test(userAgent)) {
    return 'tablet';
  } else {
    return 'desktop';
  }
}

/**
 * Calculate engagement score based on various factors
 */
export interface EngagementFactors {
  instrumentsUsed: number;
  parametersAdjusted: number;
  cellsActivated: number;
  playbackIterations: number;
  sessionDuration: number; // minutes
  aiGenerationsUsed: number;
  templatesUsed: number;
}

export function calculateEngagementScore(factors: EngagementFactors): number {
  const weights = {
    instrumentsUsed: 10,
    parametersAdjusted: 5,
    cellsActivated: 2,
    playbackIterations: 8,
    sessionDuration: 3,
    aiGenerationsUsed: 15,
    templatesUsed: 7,
  };

  let score = 0;
  score += Math.min(factors.instrumentsUsed * weights.instrumentsUsed, 100);
  score += Math.min(factors.parametersAdjusted * weights.parametersAdjusted, 100);
  score += Math.min(factors.cellsActivated * weights.cellsActivated, 100);
  score += Math.min(factors.playbackIterations * weights.playbackIterations, 100);
  score += Math.min(factors.sessionDuration * weights.sessionDuration, 100);
  score += Math.min(factors.aiGenerationsUsed * weights.aiGenerationsUsed, 100);
  score += Math.min(factors.templatesUsed * weights.templatesUsed, 100);

  // Normalize to 0-100 scale
  return Math.min(Math.round(score / 7), 100);
}

/**
 * User flow patterns that can be detected from session events
 */
export type FlowPattern = 
  | 'quick_edit'           // Short session, few changes
  | 'deep_exploration'     // Long session, many parameter adjustments
  | 'ai_assisted_creation' // Heavy AI usage
  | 'template_based'       // Started with template
  | 'from_scratch'         // No template, built from ground up
  | 'mixing_focused'       // Primarily volume/mute adjustments
  | 'experimental';        // Many instrument changes, parameter tweaks

/**
 * Flow pattern detector analyzes session events to identify user behavior patterns
 */
export class FlowPatternDetector {
  /**
   * Analyze a session's events to determine the primary user flow pattern
   */
  analyzeSession(events: AnalyticsEvent[]): FlowPattern {
    if (events.length === 0) {
      return 'from_scratch';
    }

    const eventCounts = this.countEventTypes(events);
    const sessionDuration = this.calculateDuration(events);
    
    // AI-assisted if >3 AI generations
    if ((eventCounts['ai_generation_start'] || 0) > 3) {
      return 'ai_assisted_creation';
    }
    
    // Template-based if started with template
    if (events[0]?.name === 'template_load') {
      return 'template_based';
    }
    
    // Deep exploration if >30 parameter adjustments
    if ((eventCounts['parameter_adjust'] || 0) > 30) {
      return 'deep_exploration';
    }
    
    // Mixing focused if >50% events are volume/mute
    const mixingEvents = (eventCounts['volume_adjust'] || 0) + (eventCounts['mute_toggle'] || 0);
    if (events.length > 0 && mixingEvents / events.length > 0.5) {
      return 'mixing_focused';
    }
    
    // Experimental if >5 instrument changes
    if ((eventCounts['instrument_change'] || 0) > 5) {
      return 'experimental';
    }
    
    // Quick edit if <5 minutes and <20 events (check last to avoid false positives)
    if (sessionDuration < 300000 && events.length < 20) {
      return 'quick_edit';
    }
    
    return 'from_scratch';
  }
  
  private countEventTypes(events: AnalyticsEvent[]): Record<string, number> {
    return events.reduce((counts, event) => {
      counts[event.name] = (counts[event.name] || 0) + 1;
      return counts;
    }, {} as Record<string, number>);
  }
  
  private calculateDuration(events: AnalyticsEvent[]): number {
    if (events.length < 2) return 0;
    const lastEvent = events[events.length - 1];
    const firstEvent = events[0];
    if (!lastEvent || !firstEvent) return 0;
    return lastEvent.timestamp - firstEvent.timestamp;
  }
}

/**
 * Mixing strategies that can be identified from volume data
 */
export type MixingStrategy = 
  | 'balanced'      // Similar volumes across tracks
  | 'lead_focused'  // One track significantly louder
  | 'bass_heavy'    // Bass instruments louder
  | 'dynamic';      // Frequent volume changes

/**
 * Volume data for mixing analysis
 */
export interface VolumeData {
  rowIndex: number;
  instrumentType: InstrumentType;
  volume: number;
}

/**
 * Mixing pattern analyzer identifies mixing strategies from volume data
 */
export class MixingPatternAnalyzer {
  /**
   * Analyze volume distribution to determine mixing strategy
   */
  analyzeMixingPattern(volumeData: VolumeData[]): MixingStrategy {
    if (volumeData.length === 0) return 'balanced';
    
    const volumes = volumeData.map(d => d.volume);
    const avgVolume = volumes.reduce((a, b) => a + b, 0) / volumes.length;
    const maxVolume = Math.max(...volumes);
    
    // Check for bass-heavy first (more specific)
    const bassVolumes = volumeData
      .filter(d => this.isBassInstrument(d.instrumentType))
      .map(d => d.volume);
    
    if (bassVolumes.length > 0) {
      const avgBassVolume = bassVolumes.reduce((a, b) => a + b, 0) / bassVolumes.length;
      if (avgBassVolume > avgVolume * 1.2) {
        return 'bass_heavy';
      }
    }
    
    // Check for balanced (low variance)
    const variance = this.calculateVariance(volumes);
    if (variance < 0.1) {
      return 'balanced';
    }
    
    // Check for lead-focused (one track >30% louder than average)
    if (maxVolume > avgVolume * 1.3) {
      return 'lead_focused';
    }
    
    return 'dynamic';
  }
  
  private isBassInstrument(type: InstrumentType): boolean {
    const bassTypes = ['bass-', 'amsynth', 'metal', 'membrane'];
    return bassTypes.some(bassType => type.toLowerCase().includes(bassType));
  }
  
  private calculateVariance(values: number[]): number {
    if (values.length === 0) return 0;
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
    return squaredDiffs.reduce((a, b) => a + b, 0) / values.length;
  }
}

/**
 * Session metrics aggregator for collecting and calculating session-level metrics
 */
export interface SessionMetrics {
  instrumentsUsed: Set<string>;
  parametersAdjusted: number;
  cellsActivated: number;
  playbackIterations: number;
  aiGenerationsUsed: number;
  templatesUsed: number;
  volumeAdjustments: VolumeData[];
  events: AnalyticsEvent[];
}

export class SessionMetricsAggregator {
  private metrics: SessionMetrics = {
    instrumentsUsed: new Set(),
    parametersAdjusted: 0,
    cellsActivated: 0,
    playbackIterations: 0,
    aiGenerationsUsed: 0,
    templatesUsed: 0,
    volumeAdjustments: [],
    events: [],
  };

  /**
   * Add an event to the session metrics
   */
  addEvent(event: AnalyticsEvent): void {
    this.metrics.events.push(event);

    // Update metrics based on event type
    switch (event.name) {
      case 'instrument_select':
      case 'instrument_change':
        if (event.data['instrumentType']) {
          this.metrics.instrumentsUsed.add(event.data['instrumentType'] as string);
        }
        break;
      
      case 'parameter_adjust':
      case 'parameter_complete':
        this.metrics.parametersAdjusted++;
        break;
      
      case 'grid_cell_toggle':
        if (event.data['activeState']) {
          this.metrics.cellsActivated++;
        }
        break;
      
      case 'playback_start':
        this.metrics.playbackIterations++;
        break;
      
      case 'ai_generation_start':
        this.metrics.aiGenerationsUsed++;
        break;
      
      case 'template_load':
        this.metrics.templatesUsed++;
        break;
      
      case 'volume_adjust':
        if (event.data['rowIndex'] !== undefined && 
            event.data['instrumentType'] && 
            event.data['newVolume'] !== undefined) {
          this.metrics.volumeAdjustments.push({
            rowIndex: event.data['rowIndex'] as number,
            instrumentType: event.data['instrumentType'] as string,
            volume: event.data['newVolume'] as number,
          });
        }
        break;
    }
  }

  /**
   * Get current session metrics
   */
  getMetrics(): SessionMetrics {
    return {
      ...this.metrics,
      instrumentsUsed: new Set(this.metrics.instrumentsUsed),
      volumeAdjustments: [...this.metrics.volumeAdjustments],
      events: [...this.metrics.events],
    };
  }

  /**
   * Calculate engagement score for the current session
   */
  calculateEngagementScore(sessionDurationMinutes: number): number {
    const factors: EngagementFactors = {
      instrumentsUsed: this.metrics.instrumentsUsed.size,
      parametersAdjusted: this.metrics.parametersAdjusted,
      cellsActivated: this.metrics.cellsActivated,
      playbackIterations: this.metrics.playbackIterations,
      sessionDuration: sessionDurationMinutes,
      aiGenerationsUsed: this.metrics.aiGenerationsUsed,
      templatesUsed: this.metrics.templatesUsed,
    };

    return calculateEngagementScore(factors);
  }

  /**
   * Detect the primary user flow pattern
   */
  detectFlowPattern(): FlowPattern {
    const detector = new FlowPatternDetector();
    return detector.analyzeSession(this.metrics.events);
  }

  /**
   * Analyze mixing pattern from volume adjustments
   */
  analyzeMixingPattern(): MixingStrategy {
    const analyzer = new MixingPatternAnalyzer();
    return analyzer.analyzeMixingPattern(this.metrics.volumeAdjustments);
  }

  /**
   * Get primary activities (top 3 event types by count)
   */
  getPrimaryActivities(): string[] {
    const eventCounts: Record<string, number> = {};
    
    this.metrics.events.forEach(event => {
      eventCounts[event.name] = (eventCounts[event.name] || 0) + 1;
    });

    return Object.entries(eventCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 3)
      .map(([name]) => name);
  }

  /**
   * Calculate feature time spent (simplified - based on event distribution)
   */
  getFeatureTimeSpent(totalDuration: number): Record<string, number> {
    const featureEvents: Record<string, string[]> = {
      grid_editing: ['grid_cell_toggle', 'note_pitch_change', 'grid_drag', 'row_action'],
      parameter_adjustment: ['parameter_adjust', 'parameter_complete', 'custom_params_toggle'],
      playback: ['playback_start', 'playback_stop', 'bpm_change'],
      ai_generation: ['ai_generation_start', 'ai_generation_complete', 'ai_generation_error'],
      mixing: ['volume_adjust', 'mute_toggle'],
      templates: ['template_load', 'template_save'],
    };

    const featureCounts: Record<string, number> = {};
    let totalEvents = 0;

    // Count events per feature
    this.metrics.events.forEach(event => {
      for (const [feature, eventNames] of Object.entries(featureEvents)) {
        if (eventNames.includes(event.name)) {
          featureCounts[feature] = (featureCounts[feature] || 0) + 1;
          totalEvents++;
          break;
        }
      }
    });

    // Distribute time proportionally
    const featureTimeSpent: Record<string, number> = {};
    for (const [feature, count] of Object.entries(featureCounts)) {
      featureTimeSpent[feature] = totalEvents > 0 
        ? Math.round((count / totalEvents) * totalDuration)
        : 0;
    }

    return featureTimeSpent;
  }

  /**
   * Reset metrics for a new session
   */
  reset(): void {
    this.metrics = {
      instrumentsUsed: new Set(),
      parametersAdjusted: 0,
      cellsActivated: 0,
      playbackIterations: 0,
      aiGenerationsUsed: 0,
      templatesUsed: 0,
      volumeAdjustments: [],
      events: [],
    };
  }
}
